import { useState, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import {
  ExpandMoreOutlined,
  VerticalAlignTopOutlined,
  FileCopyOutlined,
  SupportAgentOutlined,
  AddOutlined,
  VisibilityOutlined,
  MessageOutlined,
} from '@mui/icons-material';
import {
  exportTicket,
  exportOrder,
  cloneTicket,
  notifySupport,
  $ticket,
  $works,
  $isAvailableTehzor,
  $isSendedToTehzor,
  sendTicketToTehzor,
} from '../../models';
import { $sduiTicketData } from '../../models/detail-sdui';
import { $openedId } from '../../models/detail-sdui-opened-id';
import { setShowEnterMessageToApplicant } from '../../models/related-tickets';
import * as Styled from './styled';
import { styles } from './styles';

const ticketStatusesToAddInpections = ['done', 'closed'];
const notTicketClassesToAddInpections = ['inspection'];

export const TicketButtonActions = ({
  noBorderRadius,
  disabled,
  isSdui,
}: {
  noBorderRadius?: boolean;
  disabled?: boolean;
  isSdui?: boolean;
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [ticket, works, isAvailableTehzor, isSendedToTehzor, openedId, sduiTicketData] =
    useUnit([
      $ticket,
      $works,
      $isAvailableTehzor,
      $isSendedToTehzor,
      $openedId,
      $sduiTicketData,
    ]);

  const open = Boolean(anchorEl);

  const worksData = works[0] ?? {};
  const isWorksOrTechcards = !!worksData.techCards?.length || !!worksData.works?.length;

  const ticketClass =
    typeof ticket.class === 'string' ? ticket.class : ticket.class.title;

  const ticketStatus =
    typeof ticket.status === 'string' ? ticket.status : ticket.status.title;

  const isThisTicketQourceForInspection = ticket.inspection?.id;

  const canAddInspection =
    isWorksOrTechcards &&
    !notTicketClassesToAddInpections.includes(ticketClass) &&
    ticketStatusesToAddInpections.includes(ticketStatus) &&
    !isThisTicketQourceForInspection;

  const handleClick = (event: MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(event.currentTarget);

  const handleClose = () => setAnchorEl(null);

  const handleExport = () => {
    exportTicket();
    handleClose();
  };

  const handleExportOrder = () => {
    exportOrder();
    handleClose();
  };

  const handleClone = () => {
    cloneTicket();
    handleClose();
  };

  const handleSupport = () => {
    const newVersion = localStorage.getItem('ticketsNewVersion') === 'new' ? true : false;
    const id = newVersion ? openedId! : ticket.id!;

    notifySupport(Number(id));
    handleClose();
  };

  const handleSendToTehzor = () => {
    sendTicketToTehzor();
  };

  const isDisabledSupport = () => {
    if (!isSdui) return ticket.isActiveSupport;

    return !!sduiTicketData.channels?.support?.id;
  };

  const isIncident = sduiTicketData.class === 'incident';

  const showEnterMessageToApplicant = () => {
    setShowEnterMessageToApplicant(true);
    handleClose();
  };

  return (
    <>
      <Styled.Actions
        color="primary"
        variant="outlined"
        endIcon={<ExpandMoreOutlined />}
        onClick={handleClick}
        noBorderRadius={noBorderRadius}
        disabled={disabled}
      >
        {t('Label.actions')}
      </Styled.Actions>
      <Menu
        id="ticket-actions-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {isIncident && (
          <MenuItem onClick={showEnterMessageToApplicant}>
            <ListItemIcon>
              <MessageOutlined />
            </ListItemIcon>
            <ListItemText>{t('SendMessageToApplicants')}</ListItemText>
          </MenuItem>
        )}

        <MenuItem onClick={handleExport}>
          <ListItemIcon>
            <VerticalAlignTopOutlined />
          </ListItemIcon>
          <ListItemText>{t('ExportTicket')}</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleExportOrder}>
          <ListItemIcon>
            <VerticalAlignTopOutlined />
          </ListItemIcon>
          <ListItemText>{t('OrderOutfit')}</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleClone}>
          <ListItemIcon>
            <FileCopyOutlined />
          </ListItemIcon>
          <ListItemText>{t('CloneTicket')}</ListItemText>
        </MenuItem>

        {isAvailableTehzor && (
          <MenuItem onClick={handleSendToTehzor} disabled={isSendedToTehzor}>
            <ListItemIcon>
              <VisibilityOutlined />
            </ListItemIcon>
            <ListItemText>{t('SendToTehzor')}</ListItemText>
          </MenuItem>
        )}

        <MenuItem disabled={isDisabledSupport()} onClick={handleSupport}>
          <ListItemIcon>
            <SupportAgentOutlined />
          </ListItemIcon>
          <ListItemText>{t('SendToSupport')}</ListItemText>
        </MenuItem>

        {canAddInspection && (
          <Link
            style={styles.link}
            to={{
              pathname: '/inspections',
              state: {
                tck_ticket_source_id: ticket.id,
              },
            }}
          >
            <MenuItem>
              <ListItemIcon>
                <AddOutlined />
              </ListItemIcon>
              <ListItemText>{t('CheckLists.AddInspection')}</ListItemText>
            </MenuItem>
          </Link>
        )}
      </Menu>
    </>
  );
};
